import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AutoPricingCompQuantitiesSelectorV2 } from 'src/components/AutoPricing/AutoPricingCompQuantitiesSelector/AutoPricingCompQuantitiesSelectorV2';
import {
  Content,
  getContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useGetVenueZoneMapConfigInfo } from 'src/contexts/EventMapContext/useGetVenueZoneMapConfigInfo';
import { useGetVenueZoneMapInfoOverrides } from 'src/contexts/EventMapContext/useGetVenueZoneMapConfigInfoOverrides';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { Stack } from 'src/core/ui';
import { SectionSelectDialog } from 'src/dialogs/SectionSelectDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useGetEventAutoPricingSettings } from 'src/hooks/useGetEventAutoPricingSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { VENUE_ZONE_CONFIG_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  Feature,
  ListingDetailsAutoPricingSectionUpdatesV2,
  SectionSectionGroup,
} from 'src/WebApiController';

import { AutoPricingSettingsInput } from '../../BulkEditAutoPricingSettingsDialog.types';
import { useAutoPricingSettings } from '../../useBulkAutoPricingSettings';
import { BulkEditAutoPricingSettingsSectionSelector } from '../BulkEditAutoPricingSettingsSectionSelector/BulkEditAutoPricingSettingsSectionSelector';
import { PricingSetting } from '../PricingSetting';
import { SettingsSubheader } from '../PricingSubheader';

type PricingAgainstProps = {
  origin: Pick<
    AutoPricingSettingsInput,
    | 'compListingMode'
    | 'compListingQuantityScoreAdjustmentEnabled'
    | 'compListingQuantityScoreAdjustmentOverrideJson'
    | 'compListingOnlyForSelectedSectionsEnabled'
    | 'compListingSelectedSectionSettings'
    | 'undercutAbsoluteAmount'
    | 'undercutRelativeAmount'
  >;
  compListingModes: Record<string, ContentId>;
  compListingModesDisabled: Record<string, ContentId | undefined>;
  canUseZoneMap?: boolean;
};

export const PricingAgainst: React.FC<PricingAgainstProps> = ({
  origin,
  compListingModes,
  compListingModesDisabled,
  canUseZoneMap,
}) => {
  const {
    compListingMode,
    compListingQuantityScoreAdjustmentOverrideJson,
    compListingSelectedSectionSettings,
    useDefaultQuantityFilters,
    onCompListingChange,
    onCompListingModeChange,
  } = useAutoPricingSettings();

  const hasAllowSeatScoreCompListingModeFeature = useUserHasFeature(
    Feature.AllowSeatScoreCompListingMode
  );

  const { watch, setValue } =
    useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();

  const { event } = useEventMapContext();
  const contentContext = useContentContext();

  const [overrideLookup, setOverrideLookup] = useState<
    | {
        [key: string]: SectionSectionGroup;
      }
    | null
    | undefined
  >(null);

  const [defaultLookup, setDefaultLookup] = useState<
    | {
        [key: string]: SectionSectionGroup;
      }
    | null
    | undefined
  >(null);

  const { pricingSettingsQuery } = useGetEventAutoPricingSettings(
    event ?? null
  );

  const venueZoneMapConfigInfoQuery = useGetVenueZoneMapConfigInfo(
    event?.viagId,
    event?.venCfgId
  );

  const venueZoneMapConfigOverrideInfoQuery = useGetVenueZoneMapInfoOverrides(
    event?.viagId,
    event?.venCfgId
  );

  const selectedOverrideId = pricingSettingsQuery.data?.venueZoneOverrideId;
  const defaultConfig =
    pricingSettingsQuery.data?.venueZoneConfigType ??
    venueZoneMapConfigInfoQuery.data?.defaultVenueZoneConfigType;

  const venueNameFromQuery = useMemo(() => {
    if (
      selectedOverrideId != null &&
      venueZoneMapConfigOverrideInfoQuery.data &&
      venueZoneMapConfigOverrideInfoQuery.data[selectedOverrideId]?.name != null
    ) {
      return venueZoneMapConfigOverrideInfoQuery.data[selectedOverrideId].name;
    }

    if (
      defaultConfig != null &&
      venueZoneMapConfigInfoQuery.data?.venueMapZoneConfigs[defaultConfig]
    ) {
      return getContent(
        VENUE_ZONE_CONFIG_TYPE_TO_CID[defaultConfig],
        contentContext
      );
    }
  }, [
    contentContext,
    defaultConfig,
    selectedOverrideId,
    venueZoneMapConfigInfoQuery.data?.venueMapZoneConfigs,
    venueZoneMapConfigOverrideInfoQuery.data,
  ]);

  const currentVenueName = watch('venueZoneMapName');

  useEffect(() => {
    if (
      overrideLookup == null &&
      selectedOverrideId != null &&
      venueZoneMapConfigOverrideInfoQuery.data &&
      venueZoneMapConfigOverrideInfoQuery.data[selectedOverrideId] &&
      !venueZoneMapConfigOverrideInfoQuery.isLoading
    ) {
      setOverrideLookup(
        venueZoneMapConfigOverrideInfoQuery.data[selectedOverrideId]
          .sectionSectionGroupLookup
      );
    }
  }, [
    overrideLookup,
    selectedOverrideId,
    venueZoneMapConfigOverrideInfoQuery.data,
    venueZoneMapConfigOverrideInfoQuery.isLoading,
    setValue,
  ]);

  useEffect(() => {
    if (
      defaultLookup == null &&
      defaultConfig != null &&
      venueZoneMapConfigInfoQuery.data &&
      !venueZoneMapConfigInfoQuery.isLoading
    ) {
      setDefaultLookup(
        venueZoneMapConfigInfoQuery.data.venueMapZoneConfigs[defaultConfig]
      );
    }
  }, [
    defaultLookup,
    defaultConfig,
    venueZoneMapConfigInfoQuery.data,
    venueZoneMapConfigInfoQuery.isLoading,
    contentContext,
    setValue,
  ]);

  useEffect(() => {
    if (venueNameFromQuery && currentVenueName !== venueNameFromQuery) {
      setValue('venueZoneMapName', venueNameFromQuery);
    }
  }, [currentVenueName, venueNameFromQuery, setValue]);

  const sectionSelectDialog = useBasicDialog();

  const onResetCompListingMode = useCallback(() => {
    onCompListingChange({
      compListingModeNew: origin.compListingMode.value,
      compListingQuantityScoreAdjustmentEnabledNew:
        origin.compListingQuantityScoreAdjustmentEnabled.value,
      compListingOnlyForSelectedSectionsEnabledNew:
        origin.compListingOnlyForSelectedSectionsEnabled?.value,
      compListingSelectedSectionSettingsNew:
        origin.compListingSelectedSectionSettings?.value,
    });
  }, [
    onCompListingChange,
    origin.compListingMode.value,
    origin.compListingOnlyForSelectedSectionsEnabled?.value,
    origin.compListingQuantityScoreAdjustmentEnabled.value,
    origin.compListingSelectedSectionSettings?.value,
  ]);

  const quantitiesFilterHasChange = useMemo(
    () =>
      Boolean(
        origin.compListingQuantityScoreAdjustmentOverrideJson?.value !==
          compListingQuantityScoreAdjustmentOverrideJson?.value ||
        (useDefaultQuantityFilters &&
          origin?.compListingQuantityScoreAdjustmentOverrideJson.hasConflict)
      ),
    [
      compListingQuantityScoreAdjustmentOverrideJson?.value,
      origin.compListingQuantityScoreAdjustmentOverrideJson,
      useDefaultQuantityFilters,
    ]
  );
  const onResetQuantitiesFilter = useCallback(() => {
    (onCompListingChange({
      compListingQuantityScoreAdjustmentOverrideJsonNew:
        origin.compListingQuantityScoreAdjustmentOverrideJson?.value,
    }),
      setValue('useDefaultQuantityFilters', false));
  }, [
    onCompListingChange,
    origin.compListingQuantityScoreAdjustmentOverrideJson?.value,
    setValue,
  ]);

  return (
    <>
      <SettingsSubheader title={ContentId.PriceAgainst} />
      {hasAllowSeatScoreCompListingModeFeature && (
        <PricingSetting
          header={<Content id={ContentId.AllListings} />}
          detail={
            <PosEnumSelect
              value={compListingMode?.value}
              valueOptionsContent={compListingModes}
              valueOptionsDisabled={compListingModesDisabled}
              placeholderText={ContentId.None}
              onClick={(e) => e.stopPropagation()}
              onChange={onCompListingModeChange}
              style={{ width: '50%' }}
            />
          }
          hasChange={origin.compListingMode.value !== compListingMode?.value}
          hasConflict={origin.compListingMode.hasConflict}
          onReset={onResetCompListingMode}
        />
      )}

      <BulkEditAutoPricingSettingsSectionSelector
        origin={origin}
        canUseZoneMap={canUseZoneMap}
      />

      <PricingSetting
        header={<Content id={ContentId.QuantitiesFilter} />}
        detail={
          <Stack direction="row" width="full">
            <div style={{ width: '50%' }}>
              <AutoPricingCompQuantitiesSelectorV2
                quantityScoreAdjustmentOverrideJson={
                  compListingQuantityScoreAdjustmentOverrideJson?.value
                }
                onUpdateQuantityFilters={(
                  _,
                  quantityFilterScoreAdjustmentOverrideJson,
                  useDefaultQuantityFilters
                ) => {
                  onCompListingChange({
                    compListingQuantityScoreAdjustmentOverrideJsonNew:
                      quantityFilterScoreAdjustmentOverrideJson,
                  });
                  if (useDefaultQuantityFilters != null) {
                    setValue(
                      'useDefaultQuantityFilters',
                      useDefaultQuantityFilters
                    );
                  }
                }}
                rootStyle={{ width: '100%' }}
                bulkProps={{
                  hasChanged: Boolean(
                    compListingQuantityScoreAdjustmentOverrideJson?.hasChanged
                  ),
                }}
              />
            </div>
          </Stack>
        }
        hasChange={quantitiesFilterHasChange}
        hasConflict={
          origin.compListingQuantityScoreAdjustmentOverrideJson?.hasConflict
        }
        onReset={onResetQuantitiesFilter}
      />
      <SectionSelectDialog
        {...sectionSelectDialog.dialogProps}
        initialSectionIds={
          compListingSelectedSectionSettings?.value?.sectionIdFilter ?? []
        }
        onSave={(selectedSections) => {
          const compListingSelectedSectionSettingsNew = {
            ...compListingSelectedSectionSettings?.value,
            sectionIdFilter: selectedSections.map((s) => s.id),
            rowIdFilter:
              compListingSelectedSectionSettings?.value?.rowIdFilter ?? [],
            sectionRowIdFilter:
              compListingSelectedSectionSettings?.value?.sectionRowIdFilter ??
              {},
          };
          onCompListingChange({ compListingSelectedSectionSettingsNew });
          sectionSelectDialog.closeDialog();
        }}
        onClosed={sectionSelectDialog.closeDialog}
      />
    </>
  );
};
